import React, { useCallback, useContext, useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import {
  getCampaignCalculationTypesAction,
  getCampaignTypesAction,
} from '@/store/trading-accounts/trading-accounts.actions'
import { useApiClient } from '@/utils/api/ApiClient'
import { SortDescriptor } from '@progress/kendo-data-query'
import { GridColumn as Column, Grid, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import { MarketingCampaignDto } from 'api/Trading-Accounts/Types/MarketingCampaignDto'
import AddButton from 'components/AddButton/AddButton'
import { Pager } from 'components/components/Pager'
import { Modal } from 'components/CustomModal'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { getWalletFilterFields } from 'components/Filter/marketingCampaignFields'
import { Link } from 'components/Link'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import { CompositeFilterDescriptor, GridFilterActionState } from 'store/grid'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import CampaignDetailsPage from './CampaignDetailsPage/CampaignDetailsPage'
import { campaignsColumn } from './campaignsColumn'

const CampaignsOverviewPage = (): JSX.Element => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const dispatch = useAppDispatch()

  const [auth] = useContext(AuthSessionContext)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })

  const permissions = auth?.permissions || []

  const crumbs = [{ title: 'Marketing - Campaigns', link: links.marketingCampaigns.url }]
  const [campaigns, setCampaigns] = useState<MarketingCampaignDto[]>([])

  const [isModalOpen, setModalOpen] = useState(false)

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)

  const campaignTypes = useAppSelector((state) => state.tradingAccounts.campaignTypes)
  const campaignCalculationTypes = useAppSelector(
    (state) => state.tradingAccounts.campaignCalculationTypes
  )

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      const filterDescriptor = filter ? filter : gridState.filter
      return buildQuery(filterDescriptor) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(sortDescriptors ?? gridState.sort)
    },
    [gridState.sort]
  )

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<MarketingCampaignDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )

    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (
          gridFilterState: GridFilterActionState
        ): Promise<PageData<MarketingCampaignDto[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await tradingAccountsApiClient.getMarketingCampaigns({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount !== false,
          })

          if (isEmpty(campaignTypes)) {
            dispatch(getCampaignTypesAction(tradingAccountsApiClient))
          }
          if (isEmpty(campaignCalculationTypes)) {
            dispatch(getCampaignCalculationTypesAction(tradingAccountsApiClient))
          }

          if (response?.items) {
            setCampaigns(response.items)
          }
          setTableData({ ...tableData, meta: response })

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSortChange = async (event: GridSortChangeEvent) => {
    const sort = event.sort
    const sortQuery = getSort(sort)
    const searchQuery = getQuery()

    gridStatusActions.setFetching(true)

    const response = await tradingAccountsApiClient.getMarketingCampaigns({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })

    gridStatusActions.setFetching(false)

    gridActions.setGridFilters({
      sort,
      total: response.totalResults,
    })

    if (response?.items) {
      setCampaigns(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    gridStatusActions.setFetching(true)

    const response = await tradingAccountsApiClient.getMarketingCampaigns({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })

    gridStatusActions.setFetching(false)

    gridActions.setGridFilters({
      total: response.totalResults,
    })

    if (response?.items) {
      setCampaigns(response.items)
    }
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page

    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await tradingAccountsApiClient.getMarketingCampaigns({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })

    gridActions.setGridFilters({
      total: response.totalResults,
    })

    if (response?.items) {
      setCampaigns(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await tradingAccountsApiClient.getMarketingCampaigns({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })

    if (response?.items) {
      setCampaigns(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSort(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await tradingAccountsApiClient.getMarketingCampaigns({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setCampaigns(response.items.map((x) => Object.assign({ selected: false }, x)))
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const handleResetFilter = async () => {
    gridActions.setGridFilters({})
    await filterFetch({ query: null })
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {isModalOpen && (
        <Modal width={1200} visible={isModalOpen} onClose={() => setModalOpen(false)}>
          <CampaignDetailsPage
            afterSubmit={() => {
              setModalOpen(false)
              handleResetFilter()
            }}
          />
        </Modal>
      )}
      <Breadcrumbs crumbs={crumbs} />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.MARKETING_CAMPAIGNS_OVERVIEW}
        filterFields={getWalletFilterFields()}
        getQuery={getQuery}
      />
      <AddButton
        text='Add Marketing Campaign'
        disabled={
          !hasPermission(permissions, PermissionsEnum.CanViewMarketingCampaigns) &&
          !hasPermission(permissions, PermissionsEnum.CanEditMarketingCampaigns)
        }
        onClick={() => setModalOpen(true)}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <Grid
          {...gridState}
          resizable
          data={campaigns}
          sort={gridState.sort}
          total={gridState.total}
          take={gridState.take}
          skip={gridState.skip}
          onSortChange={handleSortChange}
          pageable={true}
          pager={() => (
            <Pager
              onSkipCountChange={handleSkipCountChange}
              onPageChange={handleGridPageChange}
              onPageSizeChange={handlePageSizeChange}
              page={gridState.pageIndex}
              pageSize={gridState.pageSize}
              take={gridState.take || 10}
              skip={gridState.skip || 0}
            />
          )}
          style={{ zIndex: 0 }}
        >
          <Column
            title='Name'
            cell={(props) => {
              if (!hasPermission(permissions, PermissionsEnum.CanViewMarketingCampaigns)) {
                return <td>{props.dataItem.name}</td>
              }
              return (
                <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
                  <Link
                    to={{
                      pathname: generatePath(links.marketingCampaigns.detailsUrl as string, {
                        id: props.dataItem.id,
                        slug: 'details',
                      }),
                    }}
                  >
                    {props.dataItem.name}
                  </Link>
                </td>
              )
            }}
          />
          {campaignsColumn
            .filter((c) => c.show)
            .map((column, idx) => (
              <Column key={idx + 2} {...column} />
            ))}
        </Grid>
      </TableStateContext.Provider>
    </React.Fragment>
  )
}

export default CampaignsOverviewPage
